import { BaseModel } from '@/api/base-model'
import { AuthClient } from '@/api/auth-client'
import { PageResult } from '@/api/types'
import axios, { CancelToken } from 'axios'

export class RestClient<T extends BaseModel<T>> extends AuthClient {
    protected collectionName!: string
    protected parseData!: (data: any) => T
    private readonly entries: Map<string, T> = new Map<string, T>()
    /** Raised when the cache is emptied: a response to a request from before then doesn't go into it */
    protected cacheGeneration: number = 0
    /** The access token the cache owner was last checked against, and the user/company the cache was filled for */
    private ownerToken: string|null = null
    private cacheOwner: string|null = null

    /**
     * The session cache. It only holds what the signed-in user may see: it is emptied as soon as another user or
     * company is signed in than the one it was filled for (a login, a logout without a page load, an impersonation),
     * however that happened.
     */
    get cache(): Map<string, T> {
        this.checkCacheOwner()

        return this.entries
    }

    /** The user and company the cache is for now (null when nobody is signed in) */
    protected get cacheOwnerKey(): string|null {
        this.checkCacheOwner()

        return this.cacheOwner
    }

    /** Empties the cache. A response to a request from before then doesn't go into it. */
    clearCache(): void {
        this.entries.clear()
        this.cacheGeneration++
    }

    /**
     * Puts a response into the cache, unless the cache was emptied after its request started (generation), also
     * because another user signed in meanwhile: that user must never get it
     */
    protected remember(id: string, data: T, generation: number): void {
        this.checkCacheOwner()

        if (generation === this.cacheGeneration) {
            this.entries.set(id, data)
        }
    }

    protected checkCacheOwner(): void {
        const accessToken = this.accessToken
        if (accessToken === this.ownerToken) {
            return
        }

        // A refreshed token of the same user keeps the cache; only another user or company empties it
        this.ownerToken = accessToken
        const token = accessToken ? this.decodedAccessToken : null
        const owner = token ? `${token.id}/${token.companyId}` : null

        if (owner !== this.cacheOwner) {
            this.cacheOwner = owner
            this.clearCache()
        }
    }

    async get(id: string, fresh: boolean = false): Promise<T> {
        try {
            // Through the getter first, so that the generation below belongs to the signed-in user's cache
            const cached = this.cache.get(id)
            if (!fresh && cached !== undefined) {
                return cached
            }

            const generation = this.cacheGeneration
            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${id}`)
            const data = this.parseData(response.data) as T
            this.remember(id, data, generation)

            return data

        } catch (error) {
            this.handleError(error)
        }
    }

    async getAll(params: any = {}, cancelToken: CancelToken = null): Promise<T[]> {
        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}`, {
                params,
                cancelToken,
            })

            const collection = response.data['hydra:member'] as any[]
            return collection.map(value => this.parseData(value))

        } catch (error) {
            this.handleError(error)
        }
    }

    async getPaged(params: any, cancelToken: CancelToken = null): Promise<PageResult<T>> {
        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}`, {
                params,
                cancelToken,
            })

            const totalItems = response.data['hydra:totalItems']
            const totalPages = Math.ceil(totalItems / (params.itemsPerPage || 10))
            const collection = (response.data['hydra:member'] as any[])
                .map(value => this.parseData(value))

            return {
                collection,
                totalItems,
                totalPages,
            }

        } catch (error) {
            this.handleError(error)
        }
    }

    async create(data: T): Promise<T> {
        try {
            const response = await this.client.post(`${this.basePath}/${this.collectionName}`, data)
            return await this.get(data.id, true)

        } catch (error) {
            this.handleError(error)
        }
    }

    async update(id: string, data: any): Promise<T> {
        try {
            const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}`, data)
            return await this.get(id, true)
        } catch (error) {
            this.handleError(error)
        }
    }

    async delete(id: string): Promise<void> {
        try {
            await this.client.delete(`${this.basePath}/${this.collectionName}/${id}`)
        } catch (error) {
            this.handleError(error)
        }
    }

    protected handleError(error: any) {
        if (axios.isCancel(error)) {
            return
        }

        if (error.response) {

            if ('hydra:description' in error.response.data) {
                console.log(error)
                // throw Error(error.response.data['hydra:description'])

            } else {

                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.log(error.response.data)
                console.log(error.response.status)
                console.log(error.response.headers)
                console.log(error.config)
            }

        } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.log(error.request)
            console.log(error.config)

        } else {
            // Something happened in setting up the request that triggered an Error
            console.log('Error', error.message)
            console.log(error.config)
        }
    }
}
